import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Loader2, Search, Users, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

const STATUS_STYLE = {
  submitted: 'bg-amber-50 text-amber-700',
  under_review: 'bg-blue-50 text-blue-700',
  closed: 'bg-gray-100 text-gray-600',
};
const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'submitted', label: 'Submitted' },
  { id: 'under_review', label: 'Under Review' },
  { id: 'closed', label: 'Closed' },
];

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

export default function SafeguardingSection({ school, openReportId, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(null);
  const [note, setNote] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/safeguarding/reports', {
        params: { status: filter, search: search.trim() || undefined },
      });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load the Safeguarding section');
    }
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  const openReport = async (id) => {
    const res = await api.get(`/safeguarding/reports/${id}`);
    setOpen(res.data);
    setNote('');
    setStatusNote('');
  };

  // Opened from the Overview "Needs your action" list
  useEffect(() => {
    if (openReportId) openReport(openReportId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openReportId]);

  const addNote = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/safeguarding/reports/${open.id}/notes`, { text: note });
      setOpen(res.data);
      setNote('');
      await load();
      onChanged?.();
      toast.success('Internal note saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the note');
    }
    setBusy(false);
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const res = await api.put(`/safeguarding/reports/${open.id}/status`, { status, note: statusNote });
      setOpen(res.data);
      setStatusNote('');
      await load();
      onChanged?.();
      toast.success(`Report marked ${res.data.status_label}. The parent has been notified.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change the status');
    }
    setBusy(false);
  };

  if (error) {
    const restricted = /restricted|authorised|not authorised|permission/i.test(error);
    return (
      <div className="bg-white rounded-2xl border border-[#EFF0F2] p-8 text-center" data-testid="safeguarding-error">
        {restricted ? (
          <>
            <Lock className="h-5 w-5 text-[#9CA3AF] mx-auto mb-2.5" />
            <p className="text-sm text-[#4B5563]">{error}</p>
            <p className="text-xs text-[#9CA3AF] mt-1.5">
              Ask your school&apos;s safeguarding lead if you need to see these reports.
            </p>
          </>
        ) : (
          <p className="text-sm text-[#DC2626]">{error}</p>
        )}
      </div>
    );
  }
  if (!data) {
    return <div className="flex justify-center py-16" data-testid="safeguarding-loading"><Loader2 className="h-5 w-5 animate-spin text-[#9CA3AF]" /></div>;
  }

  const counts = data.counts || {};

  return (
    <div className="space-y-4" data-testid="safeguarding-section">
      <div className="bg-[#FFF7F5] border border-[#FBD9D2] rounded-2xl px-4 py-3 flex gap-3">
        <Lock className="h-4 w-4 text-[#F97D6A] shrink-0 mt-0.5" />
        <p className="text-xs text-[#9A3412] leading-relaxed">
          Reports here are confidential to {school?.school_name || 'your school'}'s authorised
          safeguarding coordinators. Parents never see your internal notes; Platform Admin cannot open
          these reports at all.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {[['submitted', 'Submitted'], ['under_review', 'Review'], ['closed', 'Closed']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`bg-white rounded-2xl border p-4 text-left transition-colors ${filter === key ? 'border-[#F97D6A]' : 'border-[#EFF0F2] hover:border-[#F97D6A]/40'}`}
            data-testid={`count-${key}`}
          >
            <p className="text-2xl font-bold text-[#1F2937] font-['Outfit']">{counts[key] ?? 0}</p>
            <p className="text-[11px] sm:text-xs text-[#6B7280] mt-0.5 whitespace-nowrap">
              {key === 'under_review' ? <><span className="hidden sm:inline">Under </span>{label}</> : label}
            </p>
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9CA3AF]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search reference, child or parent"
                 className="pl-8 h-11 text-base sm:h-9 sm:text-sm rounded-lg border-gray-200 bg-white"
                 data-testid="safeguarding-search" />
        </div>
        <div className="flex gap-1.5 flex-nowrap overflow-x-auto sm:flex-wrap [&::-webkit-scrollbar]:hidden">
          {FILTERS.map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
            className={`px-3 py-1 min-h-[40px] sm:min-h-0 shrink-0 whitespace-nowrap rounded-full text-[11px] font-semibold border transition-colors ${filter === f.id ? 'bg-[#1F2937] text-white border-[#1F2937]' : 'bg-white text-[#6B7280] border-gray-200 hover:border-[#9CA3AF]'}`}
                    data-testid={`sg-filter-${f.id}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {data.reports.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#EFF0F2] p-10 text-center" data-testid="no-safeguarding-reports">
          <div className="h-11 w-11 rounded-xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
          </div>
          <p className="text-sm font-semibold text-[#1F2937]">No reports to show</p>
          <p className="text-sm text-[#6B7280] mt-1.5">Parents can raise a concern from their account menu.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="safeguarding-reports-list">
          {data.reports.map(r => (
            <button key={r.id} onClick={() => openReport(r.id)}
                    className="w-full text-left bg-white rounded-2xl border border-[#EFF0F2] px-4 py-3.5 hover:border-[#F97D6A]/40 transition-colors"
                    data-testid={`sg-report-${r.reference}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-bold text-[#1F2937]">{r.reference}</span>
                <Badge className={`border-0 text-[10px] ${STATUS_STYLE[r.status]}`}>{r.status_label}</Badge>
                <Badge className="bg-gray-50 text-gray-500 border-0 text-[10px]">{r.category_label}</Badge>
              </div>
              <p className="text-xs text-[#6B7280] mt-1 break-words">
                {r.child_name}{r.child_grade ? ` · Grade ${r.child_grade}` : ''} · reported by {r.reporter_name} · {fmtTime(r.created_at)}
              </p>
              {(r.update_count > 0 || r.note_count > 0) && (
                <p className="text-[11px] text-[#9CA3AF] mt-1">
                  {r.update_count > 0 && `${r.update_count} parent update${r.update_count > 1 ? 's' : ''}`}
                  {r.update_count > 0 && r.note_count > 0 && ' · '}
                  {r.note_count > 0 && `${r.note_count} internal note${r.note_count > 1 ? 's' : ''}`}
                </p>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#EFF0F2] p-5" data-testid="safeguarding-authorised-list">
        <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] flex items-center gap-1.5 mb-2">
          <Users className="h-3.5 w-3.5" /> Authorised at your school
        </p>
        <AuthorisedList />
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="rounded-2xl max-w-xl max-h-[90vh] overflow-y-auto" data-testid="sg-detail-dialog">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle className="font-['Outfit'] flex items-center gap-2 flex-wrap">
                  {open.reference}
                  <Badge className={`border-0 text-[10px] ${STATUS_STYLE[open.status]}`} data-testid="sg-detail-status">{open.status_label}</Badge>
                </DialogTitle>
                <DialogDescription>
                  {open.child_name}{open.child_grade ? ` · Grade ${open.child_grade}` : ''} · {open.category_label} ·
                  reported by {open.reporter_name} ({open.reporter_email})
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3 text-sm">
                {open.incident_date && <p className="text-[#6B7280]"><span className="font-semibold text-[#1F2937]">Incident date:</span> {open.incident_date}</p>}
                {open.related_activity_label && <p className="text-[#6B7280] break-words"><span className="font-semibold text-[#1F2937]">Related:</span> {open.related_activity_label}</p>}

                <div className="bg-[#F9FAFB] rounded-xl p-3.5">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] mb-1.5">Parent's report</p>
                  <p className="text-sm text-[#1F2937] whitespace-pre-wrap break-words">{open.description}</p>
                </div>

                {(open.updates || []).map((u, i) => (
                  <div key={i} className="bg-[#F9FAFB] rounded-xl p-3.5" data-testid={`sg-update-${i}`}>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] mb-1.5">Parent added · {fmtTime(u.at)}</p>
                    <p className="text-sm text-[#1F2937] whitespace-pre-wrap break-words">{u.text}</p>
                  </div>
                ))}

                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] mb-2">Confidential notes</p>
                  {(open.notes || []).length === 0 && <p className="text-xs text-[#9CA3AF] mb-2">No notes yet.</p>}
                  <div className="space-y-2">
                    {(open.notes || []).map((n, i) => (
                      <div key={i} className="border border-[#F3F4F6] rounded-xl px-3 py-2" data-testid={`sg-note-${i}`}>
                        <p className="text-[11px] text-[#9CA3AF]">{n.by} · {fmtTime(n.at)}</p>
                        <p className="text-sm text-[#1F2937] whitespace-pre-wrap break-words mt-0.5">{n.text}</p>
                      </div>
                    ))}
                  </div>
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                            placeholder="Add a confidential internal note"
                            className="mt-2 rounded-xl text-base sm:text-sm border-gray-200" data-testid="sg-note-input" />
                  <Button onClick={addNote} disabled={note.trim().length < 3 || busy}
                          className="mt-2 rounded-xl h-11 sm:h-9 text-xs bg-[#1F2937] hover:bg-[#374151] text-white w-full sm:w-auto"
                          data-testid="sg-add-note-btn">
                    Save internal note
                  </Button>
                </div>

                <div className="border-t border-[#F3F4F6] pt-3">
                  <Label>Change status</Label>
                  <Textarea value={statusNote} onChange={(e) => setStatusNote(e.target.value)} rows={2}
                            placeholder={open.status === 'closed' ? 'Reason for reopening' : 'Resolution note (required to close)'}
                            className="mt-1 rounded-xl text-base sm:text-sm border-gray-200" data-testid="sg-status-note-input" />
                  <p className="text-[11px] text-[#9CA3AF] mt-1">
                    {open.status === 'closed'
                      ? 'At least 5 characters to reopen.'
                      : 'A resolution note of at least 10 characters is required to close a report.'}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 mt-2">
                    {open.status !== 'under_review' && (
                      <Button onClick={() => setStatus('under_review')} disabled={busy}
                              variant="outline" className="rounded-xl h-11 sm:h-9 text-xs flex-1" data-testid="sg-review-btn">
                        {open.status === 'closed' ? 'Reopen for review' : 'Mark under review'}
                      </Button>
                    )}
                    {open.status !== 'closed' && (
                      <Button onClick={() => setStatus('closed')} disabled={busy || statusNote.trim().length < 10}
                              className="rounded-xl h-11 sm:h-9 text-xs flex-1 bg-[#F97D6A] hover:bg-[#e86c5a] text-white"
                              data-testid="sg-close-btn">
                        Close with resolution
                      </Button>
                    )}
                  </div>
                  {open.resolution_note && (
                    <p className="text-xs text-[#6B7280] mt-2 break-words"><span className="font-semibold text-[#1F2937]">Resolution:</span> {open.resolution_note}</p>
                  )}
                </div>

                <div data-testid="sg-detail-history">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] mb-2">Activity history</p>
                  <div className="space-y-1.5">
                    {(open.history || []).map((h, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-[#6B7280]">
                        <span className="text-[#9CA3AF] shrink-0 w-28">{fmtTime(h.at)}</span>
                        <span className="text-[#1F2937] break-words">{h.action}{h.by ? ` — ${h.by}` : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const AuthorisedList = () => {
  const [people, setPeople] = useState([]);
  useEffect(() => {
    api.get('/safeguarding/overview').then(r => setPeople(r.data.authorised_coordinators || [])).catch(() => {});
  }, []);
  return (
    <div className="space-y-2">
      {people.map(p => (
        <div key={p.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 border border-[#F3F4F6] rounded-xl px-3 py-2.5"
             data-testid={`safeguarding-person-${p.id}`}>
          <span className="text-sm font-semibold text-[#1F2937]">{p.full_name}</span>
          <span className="text-xs text-[#6B7280] break-words">{p.email}</span>
        </div>
      ))}
    </div>
  );
};
